import { SpecialSymbolType } from '@/containers/symbols/SpecialSymbolType'
import { SymbolType } from '@/containers/symbols/SymbolType'
import { SYMBOL_KEY_TO_ID, SYMBOL_KEY_TO_MULTIPLIER } from '@/gameConfigs/symbolMap'
import { Model } from '@/model/Model'
import { normalizeHoldAndWinCoinValue } from '@/utils/HoldAndWinCoinValue'

export class SpinResponseParser {
    private _pendingFreeSpinsAward: {
        count: number
        positions: number[][][]
        ladderCount: number
        ladderMilestones: number[]
    } | null = null

    constructor(private readonly _model: Model) {}

    public parse(data: any, sessionToken: string, lastSpinCoinValue: number): any {
        const currentRound = this.normalizeRoundPayload(data?.current_round)
        const previousRound = this.normalizeRoundPayload(data?.previous_round)
        const resolvedRound = this.hasRoundData(currentRound) ? currentRound : previousRound
        const nextRound = this.normalizeRoundPayload(data?.next_round)
        const spinType = String(currentRound?.spin_type || '').toLowerCase()
        const nextRoundType = String(nextRound?.type || '').toLowerCase()
        const clusterWins: any[] = Array.isArray(currentRound?.cluster_wins) ? currentRound.cluster_wins : []
        const previousFreebet = this._model?.spinData?.freebet
        const previousParentType = String(
            resolvedRound?.parent_type ||
                nextRound?.parent_type ||
                (this._model?.spinData as any)?.parent_type ||
                ''
        ).toLowerCase()
        const wasFreeSpinsBefore =
            Boolean(this._model?.roundState?.isFreeSpins) ||
            Number(previousFreebet?.amount_left) > 0 ||
            previousParentType === 'freespins' ||
            previousParentType === 'freespin'
        const isNextRoundPromoSpins = this.isPromoSpinsRound(nextRound)
        const isPromoSessionActive = Boolean(this._model?.isPromoSpinsRound?.())
        const isLivePromoNextRound = isNextRoundPromoSpins && isPromoSessionActive
        const endingNestedFeatureIntoPromo =
            isPromoSessionActive && wasFreeSpinsBefore && !this.isFreeSpinsRound(nextRound) && isNextRoundPromoSpins
        const resolvedCoinValue = this.resolveSpinCoinValue(
            resolvedRound,
            isNextRoundPromoSpins && !isLivePromoNextRound ? null : nextRound,
            lastSpinCoinValue
        )
        const holdAndWinExtra = this.preserveLadderProgress(
            this.resolveHoldAndWinExtra(resolvedRound, resolvedCoinValue),
            nextRound
        )
        const roundWinCoin = this.resolveNumericValue([resolvedRound?.win_amount], 0)
        const roundWinCurrency = this.resolveNumericValue(
            [resolvedRound?.win_amount_currency, resolvedRound?.win_currency, resolvedRound?.win_amount],
            0
        )

        const firstClusterPreAval = clusterWins?.[0]?.pre_aval_matrixstr
        const initialGrid = this.parseMatrixToSlotGrid(
            firstClusterPreAval || resolvedRound?.matrix,
            resolvedRound,
            resolvedCoinValue
        )
        const slotGrid: any[] = [initialGrid]

        let flatWinningCombinations: any[] = []

        if (clusterWins.length) {
            clusterWins.forEach((clusterWin: any) => {
                const combinations = this.parseClusterWinCombinations(clusterWin, resolvedCoinValue)
                if (combinations.length) {
                    flatWinningCombinations.push(...combinations)
                }
            })
        } else {
            flatWinningCombinations = this.parseRoundPaylineCombinations(resolvedRound)
        }

        const winningCombinations: any[] = [flatWinningCombinations]

        const bonusGamesWon: any[] = Array.isArray(resolvedRound?.bonus_games_won) ? resolvedRound.bonus_games_won : []
        const awardedFreeSpins = this.resolveAwardedFreeSpins(bonusGamesWon)
        const isNextRoundFreeSpins = this.isFreeSpinsRound(nextRound)
        const isNextRoundHoldAndWin = nextRoundType === 'respins' || nextRoundType === 'respin'
        const isEnteringHoldAndWinFromFreeSpins =
            wasFreeSpinsBefore && isNextRoundHoldAndWin
        const detectedFreeSpinsPositions = this.resolveFreebetPositions(resolvedRound, initialGrid, awardedFreeSpins > 0)
        const shouldDeferFreeSpinsAward = awardedFreeSpins > 0 && isNextRoundHoldAndWin && !wasFreeSpinsBefore

        if (shouldDeferFreeSpinsAward) {
            this._pendingFreeSpinsAward = {
                count: awardedFreeSpins,
                positions: detectedFreeSpinsPositions,
                ladderCount: holdAndWinExtra.ladder_count,
                ladderMilestones: holdAndWinExtra.ladder_unlock_milestones_count.slice(),
            }
        }

        const shouldReleasePendingFreeSpins =
            this._pendingFreeSpinsAward !== null &&
            !isNextRoundHoldAndWin &&
            (spinType === 'respin' || Boolean(this._model?.spinState?.isHoldAndWinActive))
        const effectiveAwardedFreeSpins = shouldDeferFreeSpinsAward
            ? 0
            : shouldReleasePendingFreeSpins
              ? this._pendingFreeSpinsAward.count
              : awardedFreeSpins
        const freebetPositions = shouldReleasePendingFreeSpins
            ? this._pendingFreeSpinsAward.positions
            : detectedFreeSpinsPositions

        const nextSpinsLeftRaw = Number(nextRound?.spins_left)
        const previousAmountLeftRaw = Number(previousFreebet?.amount_left)
        const amountLeft = shouldReleasePendingFreeSpins
            ? isNextRoundFreeSpins && Number.isFinite(nextSpinsLeftRaw)
                ? Math.max(0, nextSpinsLeftRaw)
                : effectiveAwardedFreeSpins
            : isEnteringHoldAndWinFromFreeSpins
            ? Number.isFinite(previousAmountLeftRaw)
                ? Math.max(0, previousAmountLeftRaw - 1)
                : 0
            : endingNestedFeatureIntoPromo
            ? 0
            : (isNextRoundFreeSpins || isLivePromoNextRound) && Number.isFinite(nextSpinsLeftRaw)
              ? Math.max(0, nextSpinsLeftRaw)
              : 0

        const totalAmountRaw = Number(nextRound?.num_spins)
        const currentRoundSpinsRaw = Number(currentRound?.num_spins)
        const previousTotalRaw = Number(previousFreebet?.total_amount_given)
        const usePromoTotal = isLivePromoNextRound && !wasFreeSpinsBefore && !endingNestedFeatureIntoPromo
        const resolvedTotalAmountGiven =
            shouldReleasePendingFreeSpins
                ? effectiveAwardedFreeSpins
                : isEnteringHoldAndWinFromFreeSpins && Number.isFinite(previousTotalRaw) && previousTotalRaw > 0
                ? previousTotalRaw
                : Number.isFinite(totalAmountRaw) && totalAmountRaw > 0 && (isNextRoundFreeSpins || usePromoTotal)
                ? totalAmountRaw
                : Number.isFinite(currentRoundSpinsRaw) && currentRoundSpinsRaw > 0
                  ? currentRoundSpinsRaw
                  : Number.isFinite(previousTotalRaw) && previousTotalRaw > 0
                    ? previousTotalRaw
                    : Math.max(amountLeft + awardedFreeSpins, 0)

        const resolvedTotalFreeSpinsCoinWin = this.resolveNumericValue(
            [
                nextRound?.total_fs_win_amount,
                resolvedRound?.total_fs_win_amount,
                data?.promo_details?.total_fs_win_amount,
                previousFreebet?.total_coin_win,
                previousFreebet?.total_win,
            ],
            0
        )
        const resolvedTotalFreeSpinsCurrencyWin = this.resolveNumericValue(
            [
                nextRound?.total_fs_win_amount_currency,
                resolvedRound?.total_fs_win_amount_currency,
                nextRound?.total_fs_win_amount,
                resolvedRound?.total_fs_win_amount,
                data?.promo_details?.total_fs_win_amount_currency,
                data?.promo_details?.total_fs_win_amount,
                previousFreebet?.total_win,
            ],
            resolvedTotalFreeSpinsCoinWin
        )

        const shouldExposeFreebetData =
            !shouldDeferFreeSpinsAward &&
            (isNextRoundFreeSpins ||
                isLivePromoNextRound ||
                wasFreeSpinsBefore ||
                effectiveAwardedFreeSpins > 0)
        const effectiveHoldAndWinExtra = shouldReleasePendingFreeSpins
            ? {
                  ...holdAndWinExtra,
                  ladder_count: this._pendingFreeSpinsAward.ladderCount,
                  ladder_unlock_milestones_count: this._pendingFreeSpinsAward.ladderMilestones.slice(),
              }
            : holdAndWinExtra
        const previousPromoDetails = (this._model?.spinData as any)?.promo_details
        const incomingPromoDetails =
            data?.promo_details && typeof data.promo_details === 'object' && !Array.isArray(data.promo_details)
                ? data.promo_details
                : null
        const resolvedAmountType =
            isNextRoundFreeSpins || isLivePromoNextRound || effectiveAwardedFreeSpins > 0
                ? String(
                      nextRound?.amount_type ??
                          incomingPromoDetails?.amount_type ??
                          previousPromoDetails?.amount_type ??
                          (isLivePromoNextRound ? 3 : '')
                  )
                : ''
        const freebet = shouldExposeFreebetData
            ? {
                  amount_left: amountLeft,
                  coin_amount: Number(this._model?.fixedCoinsBet ?? 1),
                  coin_value: resolvedCoinValue,
                  logic: {
                      level: 0,
                      lines_played: 0,
                  },
                  total_amount_given: resolvedTotalAmountGiven,
                  total_coin_win: resolvedTotalFreeSpinsCoinWin,
                  total_win: resolvedTotalFreeSpinsCurrencyWin,
                                    triggered_win_amount: Number(resolvedRound?.triggered_win_amount ?? 0),
                                    triggered_win_amount_currency: Number(resolvedRound?.triggered_win_amount_currency ?? 0),
                                    triggered_win_bonus_amount: Number(resolvedRound?.triggered_win_bonus_amount ?? 0),
                                    triggered_win_bonus_amount_currency: Number(resolvedRound?.triggered_win_bonus_amount_currency ?? 0),
              }
            : null

        const spinData = {
            balance: Number(data?.player?.balance ?? data?.balance ?? 0),
            base_win_currency: roundWinCurrency,
            bonus: {
                bonus_amount: 0,
                bonus_win_currency: 0,
                freebet_count: effectiveAwardedFreeSpins,
                freebet_given: effectiveAwardedFreeSpins,
                freebets_position: freebetPositions,
                jackpot_won: [] as any[],
                multipliers: Array.isArray(currentRound?.multipliers) ? currentRound.multipliers : [],
                scatter_count: awardedFreeSpins > 0 ? freebetPositions[0].length : 0,
                scatters_position: awardedFreeSpins > 0 ? freebetPositions[0] : [],
                num_coin_symbols: holdAndWinExtra.num_coin_symbols,
            },
            currency: String(data?.player?.currency ?? ''),
            amount_type: resolvedAmountType,
            promo_details: incomingPromoDetails || (isPromoSessionActive ? previousPromoDetails : null) || null,
            freebet,
            freeround: null as any,
            spin_type: spinType,
            next_round_type: nextRoundType,
            respins_left:
                nextRoundType === 'respins'
                    ? this.resolveNumericValue([nextRound?.spins_left], 0)
                    : 0,
            respins_total:
                nextRoundType === 'respins'
                    ? this.resolveNumericValue([nextRound?.num_spins], 0)
                    : 0,
            hold_and_win_extra: effectiveHoldAndWinExtra,
            is_jackpot_win: false,
            jackpot_amount: 0,
            message: '',
            session_token: data?.session_token || sessionToken || '',
            slot_grid: slotGrid,
            status: Number(data?.status ?? 0),
            win_coin: roundWinCoin,
            win_currency: roundWinCurrency,
            winning_combinations: winningCombinations,
            // Keep raw FS totals on spinData for end-of-feature commendation / summary.
            total_fs_win_amount: resolvedTotalFreeSpinsCoinWin,
            total_fs_win_amount_currency: resolvedTotalFreeSpinsCurrencyWin,
            current_round: resolvedRound,
            next_round: nextRound,
            parent_type: String(
                resolvedRound?.parent_type ||
                    nextRound?.parent_type ||
                    (this._model?.spinData as any)?.parent_type ||
                    ''
            ),
            
            //## Check for server-capped round win. This is a flag the server sends when the round win is capped, and it can be used to trigger a max exposure popup.
            cap_amount_reached: Number(resolvedRound?.misc_prizes?.capamount_reached) === 1,
        }

        if (shouldReleasePendingFreeSpins) {
            this._pendingFreeSpinsAward = null
        }

        return spinData
    }

    private normalizeRoundPayload(roundPayload: any): any {
        if (Array.isArray(roundPayload)) {
            return roundPayload[0] || {}
        }

        return roundPayload || {}
    }

    private hasRoundData(roundData: any): boolean {
        if (!roundData || typeof roundData !== 'object') {
            return false
        }

        const hasMatrix = typeof roundData?.matrix === 'string' && roundData.matrix.trim().length > 0
        const hasFeatureDetails =
            (Array.isArray(roundData?.other_feature_win_details?.coin_index) && roundData.other_feature_win_details.coin_index.length > 0) ||
            (roundData?.other_feature_win_details && typeof roundData.other_feature_win_details === 'string' && roundData.other_feature_win_details.trim().length > 0)

        return hasMatrix || hasFeatureDetails
    }

    private preserveLadderProgress(
        holdAndWinExtra: {
            coin_index: number[]
            coin_values: number[]
            coin_types: string[]
            num_coin_symbols: number
            ladder_count: number
            ladder_unlock_milestones_count: number[]
        },
        nextRound: any
    ): {
        coin_index: number[]
        coin_values: number[]
        coin_types: string[]
        num_coin_symbols: number
        ladder_count: number
        ladder_unlock_milestones_count: number[]
    } {
        if (holdAndWinExtra.ladder_unlock_milestones_count.length) {
            return holdAndWinExtra
        }

        const previousExtra = this._model?.spinData?.hold_and_win_extra as any
        const previousMilestones = this.toNumberArray(previousExtra?.ladder_unlock_milestones_count)
        const nextMilestones = this.toNumberArray(
            nextRound?.unlock_milestones_count ?? nextRound?.ladder_info?.unlock_milestones_count
        )
        const preservedMilestones = previousMilestones.length ? previousMilestones : nextMilestones

        if (!preservedMilestones.length) {
            return holdAndWinExtra
        }

        const preservedCount = this.resolveNumericValue(
            [previousExtra?.ladder_count, nextRound?.ladder_count, nextRound?.ladder_info?.ladder_count],
            holdAndWinExtra.ladder_count
        )

        return {
            ...holdAndWinExtra,
            ladder_count: preservedCount,
            ladder_unlock_milestones_count: preservedMilestones.slice(),
        }
    }

    private resolveHoldAndWinExtra(currentRound: any, spinCoinValue: number): {
        coin_index: number[]
        coin_values: number[]
        coin_types: string[]
        num_coin_symbols: number
        ladder_count: number
        ladder_unlock_milestones_count: number[]
    } {
        const extra = currentRound?.other_feature_win_details || currentRound?.extra || {}
        const bonusGamesWon = Array.isArray(currentRound?.bonus_games_won) ? currentRound.bonus_games_won : []
        const freeSpinsBonus = bonusGamesWon.find((bonus: any) => {
            const type = String(bonus?.type || '').toLowerCase()
            return type === 'freespins' || type === 'freespin'
        })
        const ladderInfo = extra?.ladder_info && typeof extra.ladder_info === 'object' ? extra.ladder_info : null
        const coinIndex = this.toNumberArray(extra?.coin_index)
        const coinTypes = this.toStringArray(extra?.coin_types)
        const coinValues = this.toNumberArray(extra?.coin_values).map((coinValue: number, index: number) => {
            const normalized = normalizeHoldAndWinCoinValue(coinValue, coinTypes[index], spinCoinValue)
            return Number.isFinite(normalized) ? Number(normalized) : 0
        })
        const ladderUnlockMilestones = this.toNumberArray(
            ladderInfo?.unlock_milestones_count ?? freeSpinsBonus?.unlock_milestones_count
        )

        return {
            coin_index: coinIndex,
            coin_values: coinValues,
            coin_types: coinTypes,
            num_coin_symbols: this.resolveNumericValue([extra?.num_coin_symbols, coinIndex.length], 0),
            ladder_count: this.resolveNumericValue([ladderInfo?.ladder_count, freeSpinsBonus?.ladder_count], 0),
            ladder_unlock_milestones_count: ladderUnlockMilestones,
        }
    }

    private toNumberArray(value: any): number[] {
        if (!Array.isArray(value)) {
            return []
        }

        return value
            .map((item: any) => Number(item))
            .filter((item: number) => Number.isFinite(item))
    }

    private toStringArray(value: any): string[] {
        if (!Array.isArray(value)) {
            return []
        }

        return value.map((item: any) => String(item || ''))
    }

    private resolveSpinCoinValue(currentRound: any, nextRound: any, lastSpinCoinValue: number): number {
        return this.resolveNumericValue(
            [
                currentRound?.coin_value,
                nextRound?.coin_value,
                lastSpinCoinValue,
                this._model?.currentCoinBet,
                this._model?.initData?.game?.default_coin_value,
            ],
            0
        )
    }

    private toFiniteNumber(value: any): number | null {
        const parsed = Number(value)
        return Number.isFinite(parsed) ? parsed : null
    }

    private resolveNumericValue(candidates: any[], fallback: number = 0): number {
        for (const candidate of candidates) {
            const parsed = this.toFiniteNumber(candidate)
            if (parsed !== null) {
                return parsed
            }
        }

        return fallback
    }

    private isFreeSpinsRound(roundData: any): boolean {
        const type = String(roundData?.type || '').toLowerCase()
        return type === 'freespins' || type === 'freespin'
    }

    private isPromoSpinsRound(roundData: any): boolean {
        const type = String(roundData?.type || '').toLowerCase()
        return type === 'promospins' || type === 'promospin' || type.startsWith('promo')
    }

    private resolveAwardedFreeSpins(bonusGamesWon: any[]): number {
        if (!Array.isArray(bonusGamesWon) || !bonusGamesWon.length) {
            return 0
        }

        return bonusGamesWon.reduce((acc: number, item: any) => {
            const type = String(item?.type || '').toLowerCase()
            if (type !== 'freespins' && type !== 'freespin') {
                return acc
            }

            const value = Number(item?.num_spins ?? item?.spins ?? item?.amount ?? 0)
            return acc + (Number.isFinite(value) ? Math.max(0, value) : 0)
        }, 0)
    }

    private resolveFreebetPositions(currentRound: any, initialGrid: any[], detectFromGrid: boolean): number[][][] {
        const rows = Number(initialGrid?.[0]?.length || 0)
        const reels = Number(initialGrid?.length || 0)

        if (!rows || !reels) {
            return [[]]
        }

        const positions: number[][] = []

        if (detectFromGrid) {
            for (let reelIndex = 0; reelIndex < reels; reelIndex++) {
                const reel = initialGrid[reelIndex]
                if (!Array.isArray(reel)) continue

                for (let symbolIndex = 0; symbolIndex < reel.length; symbolIndex++) {
                    const symbol = reel[symbolIndex]
                    if (symbol?.id === SpecialSymbolType.SCATTER) {
                        positions.push([reelIndex, symbolIndex])
                    }
                }
            }
        }

        if (!positions.length) {
            const explicitPositions = currentRound?.bonus_games_won?.[0]?.symbol_positions
            if (Array.isArray(explicitPositions)) {
                explicitPositions.forEach((flatIndex: number) => {
                    positions.push(this.flatIndexToPosition(Number(flatIndex)))
                })
            }
        }

        return [positions]
    }

    private parseClusterWinCombinations(clusterWin: any, coinValue: number): any[] {
        const details = clusterWin?.paylinewins?.details || clusterWin?.payline_wins?.details || {}
        const clusterData = Array.isArray(details?.cluster)
            ? details.cluster
            : details?.cluster && typeof details.cluster === 'object'
              ? Object.values(details.cluster)
              : []

        const parsedCombinations = clusterData.map((item: any) => {
            const flatPositions: number[] = Array.isArray(item?.symbol_positions) ? item.symbol_positions : []
            const symbolsPositions: number[][] = flatPositions.map((flatIndex: number) =>
                this.flatIndexToPosition(flatIndex)
            )
            const symbol = String(item?.symbol || '').toLowerCase()
            const rawWinAmount = this.resolveNumericValue([item?.win_amount], 0)
            const explicitCoinWin = this.toFiniteNumber(item?.win_coin_amount)
            const explicitCurrencyWin =
                this.toFiniteNumber(item?.win_amount_currency) ??
                this.toFiniteNumber(item?.win_currency) ??
                this.toFiniteNumber(item?.base_win_currency) ??
                this.toFiniteNumber(item?.currency_win)

            const winCurrency = explicitCurrencyWin ?? rawWinAmount
            const winCoin = explicitCoinWin ?? rawWinAmount

            return {
                pay_multiplier: 0,
                symbol_count: Number(item?.num_symbols ?? symbolsPositions.length ?? 0),
                symbol_id: this.symbolKeyToId(symbol),
                symbols_positions: symbolsPositions,
                win_coin: winCoin,
                base_win_currency: winCurrency,
                win_currency: winCurrency,
            }
        })

        if (parsedCombinations.length) {
            return parsedCombinations
        }

        const fallbackWinAmount = this.resolveNumericValue(
            [
                clusterWin?.win_amount_currency,
                clusterWin?.win_currency,
                clusterWin?.base_win_currency,
                clusterWin?.win_amount,
            ],
            0
        )

        if (fallbackWinAmount <= 0) {
            return []
        }

        const fallbackFlatPositions = Array.isArray(clusterWin?.prev_won_pos)
            ? clusterWin.prev_won_pos
            : Array.isArray(clusterWin?.busted_index)
              ? clusterWin.busted_index
              : []
        const fallbackPositions = fallbackFlatPositions.map((flatIndex: number) => this.flatIndexToPosition(flatIndex))

        return [
            {
                pay_multiplier: 0,
                symbol_count: fallbackPositions.length,
                symbol_id: String(SymbolType.LP_1),
                symbols_positions: fallbackPositions,
                win_coin: fallbackWinAmount,
                base_win_currency: fallbackWinAmount,
                win_currency: fallbackWinAmount,
            },
        ]
    }

    private parseRoundPaylineCombinations(currentRound: any): any[] {
        const matrixColumns = this.resolveMatrixColumns(currentRound?.matrix)
        const detailsCandidates = [
            currentRound?.paylinewins?.details,
            currentRound?.payline_wins?.details,
            currentRound?.line_wins?.details,
        ]

        for (const details of detailsCandidates) {
            if (typeof details === 'string' && details.trim().length > 0) {
                const parsedFromString = this.parseRoundPaylineDetailsString(
                    details,
                    matrixColumns,
                    currentRound?.matrix
                )
                if (parsedFromString.length) {
                    return parsedFromString
                }
            }
        }

        let entries: any[] = []
        for (const details of detailsCandidates) {
            if (!details) {
                continue
            }

            const candidateGroups = [details?.line, details?.lines, details?.payline, details?.paylines]
            for (const candidate of candidateGroups) {
                if (Array.isArray(candidate)) {
                    entries = candidate
                    break
                }

                if (candidate && typeof candidate === 'object') {
                    entries = Object.values(candidate)
                    break
                }
            }

            if (entries.length) {
                break
            }
        }

        if (!entries.length) {
            return []
        }

        return entries
            .map((item: any) => {
                const explicitPositions = Array.isArray(item?.symbols_positions) ? item.symbols_positions : []
                const paylinePositions = Array.isArray(item?.payline?.line) ? item.payline.line : []
                const flatPositions = Array.isArray(item?.symbol_positions) ? item.symbol_positions : []

                let symbolsPositions: number[][] = []

                if (
                    explicitPositions.length &&
                    Array.isArray(explicitPositions[0]) &&
                    explicitPositions[0].length === 2
                ) {
                    symbolsPositions = explicitPositions
                } else if (
                    paylinePositions.length &&
                    Array.isArray(paylinePositions[0]) &&
                    paylinePositions[0].length === 2
                ) {
                    symbolsPositions = paylinePositions
                } else if (flatPositions.length) {
                    symbolsPositions = flatPositions.map((flatIndex: number) =>
                        this.flatIndexToPosition(flatIndex, matrixColumns)
                    )
                }

                const winCurrency = this.resolveNumericValue(
                    [item?.win_amount_currency, item?.win_currency, item?.base_win_currency, item?.win_amount],
                    0
                )

                const symbolIdSource =
                    item?.symbol_id ?? item?.symbol ?? item?.payout?.symbols?.[0]?.name ?? item?.payout?.symbols?.[0]?.id
                const paylineNumberRaw = item?.payline_number ?? item?.line_number ?? item?.payline?.number
                const paylineNumber = Number(paylineNumberRaw)

                return {
                    pay_multiplier: Number(item?.pay_multiplier ?? item?.payout?.pay_multiplier ?? 0),
                    symbol_count: Number(item?.num_symbols ?? item?.symbol_count ?? symbolsPositions.length ?? 0),
                    symbol_id: String(
                        Number.isFinite(Number(symbolIdSource))
                            ? Number(symbolIdSource)
                            : this.symbolKeyToId(String(symbolIdSource || ''))
                    ),
                    payline_number: Number.isFinite(paylineNumber) && paylineNumber > 0 ? paylineNumber : undefined,
                    symbols_positions: symbolsPositions,
                    win_coin: this.resolveNumericValue([item?.win_coin_amount, item?.win_coin, item?.win_amount], winCurrency),
                    base_win_currency: winCurrency,
                    win_currency: winCurrency,
                    payline_path:
                        paylinePositions.length && Array.isArray(paylinePositions[0]) && paylinePositions[0].length === 2
                            ? paylinePositions
                            : undefined,
                }
            })
            .filter((combination: any) => combination.win_currency > 0 && combination.symbols_positions.length > 0)
    }

    private parseRoundPaylineDetailsString(details: string, columnsOverride?: number, matrixRaw?: string): any[] {
        const rawEntries = details
            .split(';')
            .map((entry) => entry.trim())
            .filter((entry) => entry.length > 0)

        return rawEntries
            .map((entry) => {
                const parts = entry.split(':')
                if (parts.length < 6) {
                    return null
                }

                const winAmount = this.resolveNumericValue([parts[1]], 0)
                const paylineNumber = Number(parts[0]) || 0
                const symbolCount = Number(parts[3]) || 0
                const betline = String(parts[4] || '').trim().toLowerCase()
                const matrixPositionsRaw = String(parts.slice(5).join(':') || '')

                const flatPositions = matrixPositionsRaw
                    .split(',')
                    .map((value) => Number(value.trim()))
                    .filter((value) => Number.isFinite(value) && value >= 0)

                const symbolsPositions = flatPositions.map((flatIndex) =>
                    this.flatIndexToPosition(flatIndex, columnsOverride)
                )
                const symbolKey = betline.length ? betline[0] : ''
                const symbolId = this.resolveLineSymbolId(symbolKey, flatPositions, matrixRaw)
                return {
                    pay_multiplier: 0,
                    symbol_count: symbolCount || symbolsPositions.length,
                    symbol_id: String(symbolId),
                    payline_number: paylineNumber > 0 ? paylineNumber : undefined,
                    symbols_positions: symbolsPositions,
                    win_coin: winAmount,
                    base_win_currency: winAmount,
                    win_currency: winAmount,
                }
            })
            .filter((combination): combination is any => {
                return !!combination && combination.win_currency > 0 && combination.symbols_positions.length > 0
            })
    }

    private flatIndexToPosition(flatIndex: number, columnsOverride?: number): number[] {
        const safeIndex = Number(flatIndex)
        if (!Number.isFinite(safeIndex) || safeIndex < 0) {
            return [0, 0]
        }

        // Legacy longhorn maps flat matrix positions with 5 reels (x = pos % reels, y = floor(pos / reels)).
        const columns = Number(columnsOverride || this._model?.numColumns || 5)
        const safeColumns = Number.isFinite(columns) && columns > 0 ? columns : 5

        const reelIndex = safeIndex % safeColumns
        const symbolIndex = Math.floor(safeIndex / safeColumns)
        return [reelIndex, symbolIndex]
    }

    private resolveMatrixColumns(matrix: unknown): number | undefined {
        if (typeof matrix !== 'string' || !matrix.length) {
            return undefined
        }

        const firstRow = matrix.split(';').find((row) => row.trim().length > 0)
        if (!firstRow) {
            return undefined
        }

        const columns = firstRow.trim().length
        return columns > 0 ? columns : undefined
    }

    private resolveLineSymbolId(defaultSymbolKey: string, flatPositions: number[], matrixRaw?: string): number {
        if (typeof matrixRaw === 'string' && matrixRaw.length && flatPositions.length) {
            const matrixFlat = matrixRaw.split(';').join('').toLowerCase()

            for (const flatIndex of flatPositions) {
                const symbolKey = matrixFlat[flatIndex]
                if (!symbolKey) {
                    continue
                }

                const symbolId = this.symbolKeyToId(symbolKey)
                if (symbolId !== SpecialSymbolType.MULTIPLIER) {
                    return symbolId
                }
            }
        }

        return this.symbolKeyToId(defaultSymbolKey)
    }

    private parseMatrixToSlotGrid(matrix: string, currentRound?: any, spinCoinValue?: number): any[] {
        if (typeof matrix !== 'string' || !matrix.length) {
            return []
        }

        const rows = matrix
            .split(';')
            .map((row) => row.trim())
            .filter((row) => row.length > 0)

        if (!rows.length) {
            return []
        }

        const rowCount = rows.length
        const colCount = rows[0].length
        const reels: any[] = []
        const coinMetaByFlatIndex = this.resolveCoinMetaByFlatIndex(currentRound, spinCoinValue)

        for (let col = 0; col < colCount; col++) {
            reels[col] = []
            for (let row = 0; row < rowCount; row++) {
                const symbolKey = String(rows[row][col] || '').toLowerCase()
                const flatIndex = row * colCount + col
                const coinMeta = coinMetaByFlatIndex.get(flatIndex)
                const fallbackMultiplier = this.symbolKeyToMultiplier(symbolKey)
                const multiplier = coinMeta?.coinValue ?? fallbackMultiplier

                reels[col].push({
                    id: this.symbolKeyToId(symbolKey),
                    symbolKey,
                    ...(Number.isFinite(Number(spinCoinValue)) && Number(spinCoinValue) > 0
                        ? { spinCoinValue: Number(spinCoinValue) }
                        : {}),
                    ...(coinMeta?.coinType ? { coinType: coinMeta.coinType } : {}),
                    ...(Number.isFinite(coinMeta?.coinValue as number) && (coinMeta?.coinValue as number) > 0
                        ? { coinValue: coinMeta?.coinValue }
                        : {}),
                    ...(multiplier ? { multiplier } : {}),
                })
            }
        }

        return reels
    }

    private resolveCoinMetaByFlatIndex(
        currentRound?: any,
        spinCoinValue?: number
    ): Map<number, { coinValue?: number; coinType?: string }> {
        const metaByIndex = new Map<number, { coinValue?: number; coinType?: string }>()
        const extra = currentRound?.other_feature_win_details || currentRound?.extra || {}

        const indexes: number[] = Array.isArray(extra?.coin_index) ? extra.coin_index : []
        const values: any[] = Array.isArray(extra?.coin_values) ? extra.coin_values : []
        const types: any[] = Array.isArray(extra?.coin_types) ? extra.coin_types : []

        indexes.forEach((rawIndex: any, idx: number) => {
            const flatIndex = Number(rawIndex)
            if (!Number.isFinite(flatIndex) || flatIndex < 0) {
                return
            }

            const coinType = String(types[idx] || '').trim().toLowerCase() || undefined
            const coinValue = normalizeHoldAndWinCoinValue(values[idx], coinType, spinCoinValue)

            metaByIndex.set(flatIndex, {
                ...(coinValue ? { coinValue } : {}),
                ...(coinType ? { coinType } : {}),
            })
        })

        return metaByIndex
    }

    private symbolKeyToId(symbolKey: string): number {
        const normalized = String(symbolKey || '').toLowerCase()
        return SYMBOL_KEY_TO_ID[normalized] ?? SymbolType.LP_1
    }

    private symbolKeyToMultiplier(symbolKey: string): number | null {
        const normalized = String(symbolKey || '').toLowerCase()
        return SYMBOL_KEY_TO_MULTIPLIER[normalized] ?? null
    }
}
